import { Container } from "react-bootstrap";
import { useState } from "react";
import TrackVisibility from "react-on-screen";

const sections = [
  {
    id: 1,
    title: "Languages & Frameworks",
    icon: "💻",
    skills: [
      { name: "Java", percent: 90 },
      { name: "Spring Boot", percent: 85 },
      { name: "JavaScript", percent: 90 },
      { name: "ReactJS", percent: 85 },
      { name: "C / C++", percent: 90 },
      { name: "Python", percent: 80 },
      { name: "HTML & CSS", percent: 90 },
      { name: "MySQL", percent: 90 },
      { name: "Cypress", percent: 90 },
      { name: "Selenium", percent: 85 },
      { name: "Postman", percent: 85 },
      { name: "JMeter", percent: 80 },
    ],
  },
  {
    id: 2,
    title: "CI/CD Tools",
    icon: "🔄",
    skills: [
      { name: "Jenkins", percent: 85 },
      { name: "GitHub Actions", percent: 90 },
      { name: "Docker", percent: 80 },
    ],
  },
  {
    id: 3,
    title: "Data & Project Management",
    icon: "📊",
    skills: [
      { name: "Power BI", percent: 60 },
      { name: "BigQuery", percent: 60 },
      { name: "Excel", percent: 85 },
      { name: "Tableau", percent: 60 },
      { name: "Jira", percent: 80 },
      { name: "Azure DevOps", percent: 60 },
    ],
  },
];

const timeline = {
  work: [
    {
      role: "CS PhD Student — Human-Computer Interaction",
      org: "University of Illinois Chicago (UIC)",
      orgLink: "https://www.uic.edu/",
      period: "Jan 2025 – Present",
      points: [
        "Researching HCI, accessibility, and inclusive technology design",
        "Teaching assistant and graduate researcher",
      ],
    },
    {
      role: "Automation Engineer",
      org: "All Generation Tech",
      orgLink: null,
      period: "Apr 2025 – Sep 2025",
      points: [
        "Developed and maintained automation frameworks for software testing",
        "Collaborated with cross-functional teams to ensure software quality",
      ],
    },
    {
      role: "Software Quality Assurance Engineer",
      org: "Dynamic Solution Innovators",
      orgLink: "https://www.dsinnovators.com/",
      period: "Apr 2022 – Jun 2026",
      points: [
        "Developed end-to-end automation scripts & deployed via CI/CD pipeline",
        "UI, API, Performance & Behavioral testing",
        "Analysed issues & developed comprehensive test cases",
      ],
    },
  ],
  education: [
    {
      degree: "Ph.D. in Computer Science",
      school: "University of Illinois Chicago (UIC)",
      schoolLink: "https://www.uic.edu/",
      period: "Jan 2025 – Present",
      grade: null,
    },
    {
      degree: "B.Sc. in Computer Science & Engineering",
      school: "Military Institute of Science & Technology (MIST)",
      schoolLink: "https://mist.ac.bd/",
      period: "Feb 2018 – May 2022",
      grade: "CGPA: 3.31 / 4.00",
    },
    {
      degree: "Higher Secondary Certificate (HSC)",
      school: "Viqarunnisa Noon School & College",
      schoolLink: "https://www.vnsc.edu.bd/",
      period: "2017",
      grade: "GPA: 5.00 / 5.00",
    },
    {
      degree: "Secondary School Certificate (SSC)",
      school: "Ideal School & College",
      schoolLink: "https://iscm.edu.bd/",
      period: "2015",
      grade: "GPA: 5.00 / 5.00",
    },
  ],
};

export const Expertise = () => {
  const [openSection, setOpenSection] = useState(null);
  const [bgOpen, setBgOpen] = useState(false);

  const toggle = (id) => setOpenSection(openSection === id ? null : id);

  return (
    <section className="achievements" id="achievements">
      <Container>
        <TrackVisibility>
          {({ isVisible }) => (
            <div className={isVisible ? "animate__animated animate__fadeIn" : ""}>
              <span className="expertise-label">What I Know</span>
              <h2 className="expertise-h2">Expertise & Background</h2>
              <p className="expertise-desc">
                Across academia and industry, I've worked with a wide range of languages,
                frameworks, and tools — from building full-stack apps and automation pipelines
                to conducting HCI research and data analysis.
              </p>

              <div className="expertise-accordion">
                {sections.map((section) => (
                  <div
                    className={`expertise-item${openSection === section.id ? " open" : ""}`}
                    key={section.id}
                  >
                    <button
                      className="expertise-item-header"
                      onClick={() => toggle(section.id)}
                    >
                      <span className="expertise-item-title">
                        <span className="expertise-item-icon">{section.icon}</span>
                        {section.title}
                      </span>
                      <span className="expertise-chevron">
                        {openSection === section.id ? "▲" : "▼"}
                      </span>
                    </button>
                    {openSection === section.id && (
                      <div className="expertise-item-body">
                        <div className="expertise-skills-grid">
                          {section.skills.map((s, i) => (
                            <div className="expertise-skill-row" key={i}>
                              <div className="expertise-skill-meta">
                                <span className="expertise-skill-name">{s.name}</span>
                                <span className="expertise-skill-pct">{s.percent}%</span>
                              </div>
                              <div className="expertise-bar-track">
                                <div
                                  className="expertise-bar-fill"
                                  style={{ width: `${s.percent}%` }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                <div className={`expertise-item${bgOpen ? " open" : ""}`}>
                  <button
                    className="expertise-item-header"
                    onClick={() => setBgOpen(!bgOpen)}
                  >
                    <span className="expertise-item-title">
                      <span className="expertise-item-icon">🎓</span>
                      Background
                    </span>
                    <span className="expertise-chevron">{bgOpen ? "▲" : "▼"}</span>
                  </button>
                  {bgOpen && (
                    <div className="expertise-item-body">
                      <div className="bg-section-label">💼 Work Experience</div>
                      <div className="timeline">
                        {timeline.work.map((w, i) => (
                          <div className="timeline-item" key={i}>
                            <div className="timeline-dot" />
                            <div className="timeline-content">
                              <div className="timeline-header">
                                <h5>{w.role}</h5>
                                <span className="timeline-period">{w.period}</span>
                              </div>
                              {w.orgLink ? (
                                <a href={w.orgLink} target="_blank" rel="noreferrer" className="timeline-org">{w.org}</a>
                              ) : (
                                <span className="timeline-org">{w.org}</span>
                              )}
                              <ul className="timeline-points">
                                {w.points.map((p, j) => <li key={j}>{p}</li>)}
                              </ul>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="bg-section-label" style={{ marginTop: "32px" }}>📚 Education</div>
                      <div className="timeline">
                        {timeline.education.map((e, i) => (
                          <div className="timeline-item" key={i}>
                            <div className="timeline-dot" />
                            <div className="timeline-content">
                              <div className="timeline-header">
                                <h5>{e.degree}</h5>
                                <span className="timeline-period">{e.period}</span>
                              </div>
                              <a href={e.schoolLink} target="_blank" rel="noreferrer" className="timeline-org">
                                {e.school}
                              </a>
                              {e.grade && <p className="timeline-grade">{e.grade}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </TrackVisibility>
      </Container>
    </section>
  );
};
